import { twMerge } from "tailwind-merge";
import { Card, CardContent, H5 } from "@sellar-io/components";
import { ReactElement, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Link } from "react-router";
import { Icon, SpinnerLegacy } from "../../../components";
import { Alert } from "../../../components/alert/Alert";
import { useDashboardContext } from "../../../components/dashboard/Dashboard.shared";
import { NavigationBar } from "../../../components/navigation/NavigationBar";
import { PageContainer } from "../../../components/pageContainer/PageContainer";
import { ProfileImageUpload } from "../../../components/profileImageUpload/ProfileImageUpload";
import {
  ACCOUNT,
  ACCOUNT_NOTIFICATION_SETTINGS,
} from "../../../constants/routes";
import { useSellarUser } from "../../../hooks";
import { apiV2AssertError } from "../../../hooks/api/v2/apiV2Request";
import { useBusinessUserList } from "../../../hooks/api/v2/businessUser/useBusinessUserList";
import { useUser } from "../../../hooks/api/v2/user/useUser";
import { useFirebaseUser } from "../../../hooks/useFirebaseUser.shared";
import { useSellarCurrentBusiness } from "../../../hooks/useSellarCurrentBusiness.shared";
import { Mixpanel } from "../../../utils/mixpanel";
import { SmsDialog } from "../SmsDialog";
import { UserSettingsUpdateMarketingPreferencesDialog } from "./Dialogs/UserSettingsUpdateMarketingPreferencesDialog/UserSettingsUpdateMarketingPreferencesDialog";
import { UpdateJobTitleModal } from "./UpdateJobTitleModal";
import { UserSettingsName } from "./UserSettingsName";

export function UserSettings(): ReactElement | null {
  const { user } = useFirebaseUser();

  if (!user) throw new Error("Cannot find user");

  const { business } = useSellarCurrentBusiness();

  const sellarUser = useSellarUser();
  if (!sellarUser.user) throw new Error("Cannot find sellarUser");
  const businessUsersQuery = useBusinessUserList(
    {
      query: [
        {
          value: parseInt(sellarUser.user.id.toString(), 10),
          type: "UserId",
          modifier: "EQUALS",
        },
      ],
    },
    {
      enabled: !!sellarUser.user.id,
    },
  );

  const [updateJobTitleModalActive, setUpdateJobTitleModalActive] =
    useState(false);
  const [
    updateMarketingPreferenceDialogActive,
    setUpdateMarketingPreferenceDialogActive,
  ] = useState(false);
  const businessUser = businessUsersQuery.data?.pages[0]?.find(
    (userBusiness) => userBusiness.BusinessId === business?.id,
  );
  const navigate = useNavigate();
  const { isTwoPanel } = useDashboardContext();
  const userQuery = useUser(parseInt(sellarUser.user.id.toString(), 10));

  useEffect(() => {
    Mixpanel.track("User Settings Page Viewed");
  }, []);

  const [smsDialogActive, setSmsDialogActive] = useState(false);

  return (
    <>
      <NavigationBar
        title={"Your Account"}
        leftIcon={"CHEVRON_LEFT"}
        onClickLeftIcon={isTwoPanel ? undefined : () => navigate(ACCOUNT)}
      />
      <PageContainer className={"mx-auto bg-white p-6"}>
        <div>
          <Card
            className={twMerge(
              "bg-white",
              "flex flex-col divide-y divide-solid divide-gray-100 align-middle",
            )}
          >
            <CardContent className={"pt-0"}>
              <H5>Profile Image</H5>
              <ProfileImageUpload />
            </CardContent>
            {userQuery.isPending ? (
              <SpinnerLegacy />
            ) : userQuery.isError ? (
              <Alert
                message={
                  apiV2AssertError(userQuery.error)?.response?.data.message ??
                  "Unknown error"
                }
              />
            ) : (
              <>
                <CardContent>
                  <UserSettingsName user={userQuery.data} />
                </CardContent>
                <CardContent>
                  <div
                    className="cursor-pointer pb-4"
                    onClick={() => setSmsDialogActive(true)}
                  >
                    <div className="flex flex-row items-center justify-between">
                      <div className="flex flex-col">
                        <H5>Phone Number</H5>
                        <span>
                          {userQuery.data.smsNumber ??
                            "Add your mobile number to receive notifications via text message"}
                        </span>
                      </div>
                      <Icon
                        icon={"PENCIL"}
                        color={"var(--theme-secondary-color)"}
                      />
                    </div>
                  </div>
                  <SmsDialog
                    active={smsDialogActive}
                    onDismiss={() => setSmsDialogActive(false)}
                  />
                </CardContent>
              </>
            )}
            <CardContent>
              {" "}
              <div className={"pb-4"}>
                <div className="flex flex-col">
                  <H5>Login Email</H5>
                  <span>{user.firebaseUser.email}</span>
                </div>
              </div>
            </CardContent>
            <CardContent>
              <div
                className="cursor-pointer pb-4"
                onClick={() => setUpdateJobTitleModalActive(true)}
              >
                <div className="flex flex-row items-center justify-between">
                  <div className="flex flex-col">
                    <H5>Job Title</H5>
                    <span>{businessUser?.jobTitle ?? "No job title set"}</span>
                  </div>
                  <Icon
                    icon={"PENCIL"}
                    color={"var(--theme-secondary-color)"}
                  />
                </div>
              </div>
              {businessUser && (
                <UpdateJobTitleModal
                  active={updateJobTitleModalActive}
                  onDismiss={() => setUpdateJobTitleModalActive(false)}
                  businessUser={businessUser}
                />
              )}
            </CardContent>
            <CardContent>
              <Link to={ACCOUNT_NOTIFICATION_SETTINGS}>
                <div className={"pb-4"}>
                  <div className="flex flex-row items-center justify-between">
                    <div className="flex flex-col">
                      <H5>Notifications</H5>
                      <span>Get notified via email or push notification</span>
                    </div>

                    <Icon
                      icon={"CHEVRON_RIGHT"}
                      size={1}
                      color={"var(--theme-action-color)"}
                    />
                  </div>
                </div>
              </Link>
            </CardContent>
            <CardContent>
              <Link
                to={""}
                onClick={() => setUpdateMarketingPreferenceDialogActive(true)}
              >
                <div className={""}>
                  <div className="flex flex-row items-center justify-between">
                    <div className="flex flex-col">
                      <H5>Marketing preferences</H5>
                      <span>
                        Manage your permissions for updates from Sellar
                      </span>
                    </div>

                    <Icon
                      icon={"CHEVRON_RIGHT"}
                      size={1}
                      color={"var(--theme-action-color)"}
                    />
                  </div>
                </div>
              </Link>
            </CardContent>
          </Card>
        </div>
        <UserSettingsUpdateMarketingPreferencesDialog
          active={updateMarketingPreferenceDialogActive}
          onDismiss={() => setUpdateMarketingPreferenceDialogActive(false)}
        />
      </PageContainer>
    </>
  );
}
